import { getEnvironmentConfig } from '../../../config/environment';
import {
  WaitingConfig,
  WaitingSession,
  WaitingType,
  WaitingSessionStatus,
  ReelItem,
  WaitingEvent,
  WaitingSessionMetadata,
  DEFAULT_WAITING_CONFIG
} from '../types';

const config = getEnvironmentConfig();
const API_BASE = config.api.brandsApi.replace('/api/v1', '');

interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

class WaitingService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}/api/v1/waiting${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }

    const result: ApiResponse<T> = await response.json();
    return result.data;
  }

  async getConfig(brandId: string, waitingType: WaitingType): Promise<{
    configId: string | null;
    config: WaitingConfig;
  }> {
    try {
      const data = await this.request<{ configId: string | null; config: WaitingConfig }>(
        `/config/${brandId}/${waitingType}`
      );
      return data;
    } catch (error) {
      console.error('Failed to fetch waiting config:', error);
      return { configId: null, config: DEFAULT_WAITING_CONFIG };
    }
  }

  async createSession(params: {
    brandId: string;
    userId?: string;
    waitingType: WaitingType;
    configId?: string;
    metadata?: WaitingSessionMetadata;
  }): Promise<WaitingSession> {
    if (params.brandId === 'a1b2c3d4-e5f6-7890-abcd-ef1234567890') {
      return {
        id: `demo-session-${Date.now()}`,
        brandId: params.brandId,
        userId: params.userId || null,
        waitingType: params.waitingType,
        configId: params.configId || null,
        status: 'generating',
        startedAt: new Date().toISOString(),
        readyAt: null,
        completedAt: null,
        metadata: params.metadata || {}
      };
    }
    const data = await this.request<{ session: WaitingSession }>('/session', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return data.session;
  }

  async updateSessionStatus(
    sessionId: string,
    status: WaitingSessionStatus
  ): Promise<WaitingSession> {
    if (sessionId?.startsWith('demo-session-')) {
      return {
        id: sessionId,
        brandId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
        userId: null,
        waitingType: 'fashplay',
        configId: null,
        status,
        startedAt: new Date().toISOString(),
        readyAt: status === 'ready' ? new Date().toISOString() : null,
        completedAt: status === 'completed' ? new Date().toISOString() : null,
        metadata: {}
      };
    }
    const data = await this.request<{ session: WaitingSession }>(
      `/session/${sessionId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    );
    return data.session;
  }

  async getSession(sessionId: string): Promise<WaitingSession> {
    if (sessionId?.startsWith('demo-session-')) {
      return {
        id: sessionId,
        brandId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
        userId: null,
        waitingType: 'fashplay',
        configId: null,
        status: 'generating',
        startedAt: new Date().toISOString(),
        readyAt: null,
        completedAt: null,
        metadata: {}
      };
    }
    const data = await this.request<{ session: WaitingSession }>(
      `/session/${sessionId}`
    );
    return data.session;
  }

  async trackEvents(events: WaitingEvent[]): Promise<void> {
    if (events.some(e => e.sessionId?.startsWith('demo-session-'))) {
      console.log('📊 [WaitingService] Demo event tracked:', events);
      return;
    }
    try {
      await this.request<{ tracked: number }>('/events', {
        method: 'POST',
        body: JSON.stringify({ events }),
      });
    } catch (error) {
      console.error('Failed to track events:', error);
    }
  }

  async trackEvent(event: WaitingEvent): Promise<void> {
    return this.trackEvents([event]);
  }

  async getReels(
    brandId: string, 
    excludeSku?: string, 
    limit?: number,
    source: 'user' | 'brand' | 'mixed' = 'user'
  ): Promise<ReelItem[]> {
    try {
      const params = new URLSearchParams();
      if (excludeSku) params.append('excludeSku', excludeSku);
      if (limit) params.append('limit', limit.toString());
      params.append('source', source);
      
      const queryString = params.toString();
      const endpoint = `/reels/${brandId}${queryString ? `?${queryString}` : ''}`;
      
      const data = await this.request<{ reels: ReelItem[] }>(endpoint);
      return data.reels;
    } catch (error) {
      console.error('Failed to fetch reels:', error);
      return [];
    }
  }

  async getUserCreations(userId: string, limit: number = 20): Promise<ReelItem[]> {
    try {
      const endpoint = `/user/${userId}/creations?limit=${limit}`;
      const data = await this.request<{ creations: ReelItem[] }>(endpoint);
      return data.creations;
    } catch (error) {
      console.error('Failed to fetch user creations:', error);
      return [];
    }
  }
}

export const waitingService = new WaitingService();
export default waitingService;
